/** Price drivers, cost drivers and margin health for the Defend step. */
import React from 'react';
import { CRC } from './moduleInputsData';
import { money } from './spreadSplitData';
import { Contribution, Defence, MarginRow } from './spreadDefence';

const card: React.CSSProperties = {
  background: '#fff', border: `1px solid ${CRC.border}`, borderRadius: 12,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: 20,
};
const heading: React.CSSProperties = {
  fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue,
};
const sub: React.CSSProperties = { fontSize: 11.5, color: CRC.heritageGrey, marginTop: 3 };
const cell: React.CSSProperties = { fontSize: 13, color: CRC.textGrey, padding: '7px 0' };
const pct = (n: number) => `${Math.round(n * 100)}%`;

/** Horizontal share bar, so the ranking reads at a glance. */
const Bar: React.FC<{ share: number; tone: string }> = ({ share, tone }) => (
  <div style={{ background: CRC.pageBg, borderRadius: 3, height: 6, width: '100%' }}>
    <div style={{ background: tone, borderRadius: 3, height: 6, width: `${Math.max(1, Math.round(share * 100))}%` }} />
  </div>
);

const ContributionTable: React.FC<{ rows: Contribution[]; tone: string; limit?: number }> = ({ rows, tone, limit }) => {
  const shown = limit ? rows.slice(0, limit) : rows;
  const rest = rows.length - shown.length;
  const restAmount = rows.slice(shown.length).reduce((sum, r) => sum + r.amount, 0);
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
      <tbody>
        {shown.map((row) => (
          <tr key={row.label} style={{ borderTop: `1px solid ${CRC.border}` }}>
            <td style={{ ...cell, width: '34%' }}>
              {row.label}
              {row.note && <div style={{ fontSize: 10.5, color: CRC.heritageGrey }}>{row.note}</div>}
            </td>
            <td style={{ ...cell, width: '30%', paddingRight: 14 }}><Bar share={row.share} tone={tone} /></td>
            <td style={{ ...cell, width: '12%', textAlign: 'right', color: CRC.heritageGrey }}>{pct(row.share)}</td>
            <td style={{ ...cell, width: '24%', textAlign: 'right', fontWeight: 600 }}>{money(row.amount)}</td>
          </tr>
        ))}
        {rest > 0 && (
          <tr style={{ borderTop: `1px solid ${CRC.border}` }}>
            <td style={{ ...cell, color: CRC.heritageGrey }} colSpan={3}>{rest} smaller line{rest === 1 ? '' : 's'}</td>
            <td style={{ ...cell, textAlign: 'right', color: CRC.heritageGrey }}>{money(restAmount)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const flagStyle = (flag: MarginRow['flag']): React.CSSProperties => ({
  fontSize: 11, fontWeight: 600,
  color: flag === 'negative' ? CRC.readableRed : flag === 'thin' ? '#B26B00' : CRC.heritageGrey,
});

const FLAG_TEXT: Record<'negative' | 'thin', string> = {
  negative: 'cost exceeds price',
  thin: 'thin margin',
};

const MarginTable: React.FC<{ rows: MarginRow[] }> = ({ rows }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
    <thead>
      <tr>
        {['Module', 'Price', 'Cost', 'Margin'].map((h, i) => (
          <th key={h} style={{ fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: CRC.heritageGrey, textAlign: i === 0 ? 'left' : 'right', paddingBottom: 6, fontWeight: 600 }}>{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.label} style={{ borderTop: `1px solid ${CRC.border}` }}>
          <td style={cell}>
            {row.label}
            {row.flag && <div style={flagStyle(row.flag)}>{FLAG_TEXT[row.flag]}</div>}
          </td>
          <td style={{ ...cell, textAlign: 'right' }}>{money(row.price)}</td>
          <td style={{ ...cell, textAlign: 'right' }}>{money(row.cost)}</td>
          <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: row.flag === 'negative' ? CRC.readableRed : CRC.blueprintBlue }}>
            {row.margin === null ? '—' : pct(row.margin)}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const DefenceDrivers: React.FC<{ defence: Defence }> = ({ defence }) => (
  <>
    <div style={card}>
      <div style={{ display: 'flex', gap: 34, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: CRC.heritageGrey }}>Price</div>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 22, fontWeight: 700, color: CRC.readableRed }}>{money(defence.totalPrice)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: CRC.heritageGrey }}>Cost</div>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 22, fontWeight: 700, color: CRC.blueprintBlue }}>{money(defence.totalCost)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: CRC.heritageGrey }}>Margin</div>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 22, fontWeight: 700, color: (defence.totalMargin ?? 0) < 0 ? CRC.readableRed : CRC.blueprintBlue }}>
            {defence.totalMargin === null ? '—' : pct(defence.totalMargin)}
          </div>
        </div>
      </div>
      {defence.missingSpreads > 0 && (
        <div style={{ ...sub, color: CRC.readableRed, marginTop: 10 }}>
          {defence.missingSpreads} spread{defence.missingSpreads === 1 ? '' : 's'} not priced yet, so these figures are incomplete.
        </div>
      )}
    </div>

    <div style={card}>
      <div style={heading}>What drives the price</div>
      <div style={sub}>Each module's share of the quoted total, across every spread.</div>
      <ContributionTable rows={defence.priceDrivers} tone={CRC.readableRed} />
    </div>

    <div style={card}>
      <div style={heading}>What drives the cost</div>
      <div style={sub}>Internal cost lines from the engine, merged across spreads and ranked. Notes show the arithmetic where a line appears once.</div>
      <ContributionTable rows={defence.costDrivers} tone={CRC.blueprintBlue} limit={8} />
    </div>

    <div style={card}>
      <div style={heading}>Margin health</div>
      <div style={sub}>Thinnest first. A negative margin means the module's cost is above what it is being sold for.</div>
      <MarginTable rows={defence.margins} />
    </div>
  </>
);
